import React, { useEffect, useState } from "react";
import { formatINR } from "../../utils/currency";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../HOC/Axios/Axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  MdArrowBack,
  MdPerson,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdPublic,
  MdLock,
  MdVerifiedUser,
  MdAccountBalance,
  MdTrendingUp,
  MdArrowUpward,
  MdArrowDownward,
  MdSwapHoriz,
  MdCalendarToday,
} from "react-icons/md";

// ── Helpers ─────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

function InfoRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 py-3.5 border-b border-slate-100 last:border-0">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center mt-0.5">
        <Icon className="text-slate-500 text-sm" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-0.5">
          {label}
        </div>
        <div className={`text-slate-800 font-semibold text-sm break-words ${mono ? "font-mono" : ""}`}>
          {value || <span className="text-slate-300 font-normal">Not provided</span>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, gradient, iconBg }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 shadow-card"
      style={{ background: gradient }}
    >
      <div
        className="absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-15"
        style={{ background: "rgba(255,255,255,0.6)" }}
      />
      <div className="relative z-10 flex items-center justify-between">
        <div>
          <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-1">
            {label}
          </p>
          <h3 className="text-white text-xl font-bold">{value ?? "—"}</h3>
        </div>
        <div
          className="flex items-center justify-center w-10 h-10 rounded-xl"
          style={{ background: iconBg }}
        >
          <Icon className="text-white text-lg" />
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
function ViewSingleUser() {
  const params = useParams();
  const navigate = useNavigate();
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(true);

  const getSingleUserID = () => {
    setLoading(true);
    axios
      .get(
        `/user/${params.id}?bankwithdraw=true&usdtwithdraw=true&deposit=true&amount=true&trade=true`
      )
      .then((res) => {
        setDatas(res?.data?.result);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (params.id) getSingleUserID();
  }, [params.id]);

  const initials = getInitials(datas?.fullName);
  const createdDate = datas?.createdAt
    ? new Date(datas.createdAt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    : null;

  return (
    <div className="min-h-screen bg-mainColor">

      {/* ── Hero Banner ── */}
      <div
        className="relative h-48 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #312e81 100%)",
        }}
      >
        {/* decorative blobs */}
        <div
          className="absolute -top-12 -left-12 w-64 h-64 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #7c3aed, transparent)" }}
        />
        <div
          className="absolute top-4 right-10 w-40 h-40 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #a855f7, transparent)" }}
        />

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-5 left-5 flex items-center gap-2 text-white/70 hover:text-white
            bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl px-4 py-2 text-sm
            font-medium transition-all backdrop-blur-sm"
        >
          <MdArrowBack className="text-base" />
          Back
        </button>

        <div className="absolute top-5 right-5 flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-4 py-2 backdrop-blur-sm">
          <MdCalendarToday className="text-violet-300 text-sm" />
          <span className="text-white/70 text-xs font-medium">
            {createdDate ? `Joined ${createdDate}` : "—"}
          </span>
        </div>
      </div>

      <div className="px-4 tablet:px-8 pb-10">

        {/* ── Profile Card ── */}
        <div className="relative -mt-16 mb-6">
          <div className="bg-white rounded-2xl shadow-soft border border-slate-100 px-6 py-5">
            <div className="flex flex-col tablet:flex-row tablet:items-center gap-5">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {loading ? (
                  <div className="w-24 h-24 rounded-2xl bg-slate-100 animate-pulse" />
                ) : datas?.profile ? (
                  <img
                    src={datas.profile}
                    alt={datas.fullName}
                    className="w-24 h-24 rounded-2xl object-cover ring-4 ring-white shadow-md"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700
                    flex items-center justify-center text-white text-2xl font-bold ring-4 ring-white shadow-md">
                    {initials || <MdPerson className="text-4xl opacity-70" />}
                  </div>
                )}
              </div>

              {/* Name & Meta */}
              <div className="flex-1 min-w-0">
                {loading ? (
                  <div className="space-y-2">
                    <div className="h-6 bg-slate-100 rounded-lg w-48 animate-pulse" />
                    <div className="h-4 bg-slate-100 rounded-lg w-64 animate-pulse" />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h1 className="text-slate-800 text-xl font-bold capitalize">
                        {datas?.fullName || "Unknown User"}
                      </h1>
                      {datas?.country && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full
                          bg-violet-100 text-violet-700 text-xs font-semibold capitalize">
                          <MdPublic className="text-xs" />
                          {datas.country}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 text-sm">{datas?.email || "No email"}</p>
                    <div className="flex flex-wrap gap-3 mt-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5">
                        <MdVerifiedUser className="text-emerald-500" />
                        Verified Member
                      </span>
                      {datas?.creditScore != null && (
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 ${datas.creditScore >= 700
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                              : datas.creditScore >= 400
                                ? "bg-amber-50 text-amber-700 border border-amber-100"
                                : "bg-red-50 text-red-600 border border-red-100"
                            }`}
                        >
                          Credit Score: {datas.creditScore}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Balance Highlight */}
              <div className="flex-shrink-0">
                <div
                  className="rounded-2xl px-6 py-4 text-center min-w-[160px]"
                  style={{
                    background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                  }}
                >
                  <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-1">
                    Balance
                  </p>
                  <p className="text-white text-xl font-bold">
                    {loading ? (
                      <span className="opacity-50">—</span>
                    ) : (
                      formatINR(datas?.amount?.balance || 0)
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-2 laptop:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={MdAccountBalance}
            label="Total Balance"
            value={loading ? "—" : formatINR(datas?.amount?.balance || 0)}
            gradient="linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)"
            iconBg="rgba(255,255,255,0.2)"
          />
          <StatCard
            icon={MdTrendingUp}
            label="Total Trades"
            value={loading ? "—" : datas?.trade?.length ?? 0}
            gradient="linear-gradient(135deg, #065f46 0%, #059669 100%)"
            iconBg="rgba(255,255,255,0.2)"
          />
          <StatCard
            icon={MdArrowDownward}
            label="Deposits"
            value={loading ? "—" : datas?.deposit?.length ?? 0}
            gradient="linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)"
            iconBg="rgba(255,255,255,0.2)"
          />
          <StatCard
            icon={MdArrowUpward}
            label="Withdrawals"
            value={loading ? "—" : datas?.withdrawal?.length ?? 0}
            gradient="linear-gradient(135deg, #b45309 0%, #f59e0b 100%)"
            iconBg="rgba(255,255,255,0.2)"
          />
        </div>

        {/* ── Info Cards Grid ── */}
        <div className="grid grid-cols-1 tablet:grid-cols-2 gap-5 mb-6">

          {/* Contact Info */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
              <div className="bg-violet-100 rounded-xl p-2">
                <MdPerson className="text-violet-600 text-base" />
              </div>
              <h2 className="text-slate-800 font-bold text-sm">Contact Information</h2>
            </div>
            <div className="px-5">
              {loading ? (
                <div className="py-4 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-4 bg-slate-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : (
                <>
                  <InfoRow icon={MdPerson} label="Full Name" value={datas?.fullName} />
                  <InfoRow icon={MdEmail} label="Email Address" value={datas?.email} />
                  <InfoRow icon={MdPhone} label="Phone Number" value={datas?.phoneNo} />
                  <InfoRow icon={MdLocationOn} label="Address" value={datas?.address} />
                  <InfoRow icon={MdPublic} label="Country" value={datas?.country} />
                  <InfoRow icon={MdLock} label="Password" value={datas?.password} mono />
                </>
              )}
            </div>
          </div>

          {/* Trade Info */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
              <div className="bg-emerald-100 rounded-xl p-2">
                <MdSwapHoriz className="text-emerald-600 text-base" />
              </div>
              <h2 className="text-slate-800 font-bold text-sm">Trade Information</h2>
            </div>
            <div className="px-5">
              {loading ? (
                <div className="py-4 space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-4 bg-slate-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : (
                <>
                  <InfoRow
                    icon={MdAccountBalance}
                    label="Total Balance"
                    value={formatINR(datas?.amount?.balance || 0)}
                  />
                  <InfoRow
                    icon={MdTrendingUp}
                    label="Total Trades"
                    value={datas?.trade?.length?.toString() ?? "0"}
                  />
                  <InfoRow
                    icon={MdArrowDownward}
                    label="Total Withdrawals"
                    value={datas?.withdrawal?.length?.toString() ?? "0"}
                  />
                  <InfoRow
                    icon={MdArrowUpward}
                    label="Total Deposits"
                    value={datas?.deposit?.length?.toString() ?? "0"}
                  />
                  <InfoRow
                    icon={MdVerifiedUser}
                    label="Credit Score"
                    value={datas?.creditScore?.toString() ?? "0"}
                  />
                  <InfoRow
                    icon={MdCalendarToday}
                    label="Member Since"
                    value={createdDate}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Legal Documents ── */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
            <div className="bg-blue-100 rounded-xl p-2">
              <MdVerifiedUser className="text-blue-600 text-base" />
            </div>
            <div>
              <h2 className="text-slate-800 font-bold text-sm">Legal Documents</h2>
              <p className="text-slate-400 text-xs mt-0.5">Identity verification documents</p>
            </div>
          </div>
          <div className="p-5">
            {loading ? (
              <div className="h-72 bg-slate-100 rounded-xl animate-pulse" />
            ) : datas?.validId ? (
              <div className="grid grid-cols-1 tablet:grid-cols-2 gap-5">
                <div className="group relative overflow-hidden rounded-xl border border-slate-200">
                  <img
                    src={datas.validId}
                    alt="Valid ID"
                    className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                    <div className="w-full bg-black/60 px-4 py-3">
                      <p className="text-white text-sm font-semibold">Valid ID Document</p>
                      <p className="text-white/70 text-xs">Uploaded by user</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-4">
                  <MdVerifiedUser className="text-slate-300 text-3xl" />
                </div>
                <p className="text-slate-600 font-semibold">No documents uploaded</p>
                <p className="text-slate-400 text-sm mt-1">
                  This user hasn't uploaded any identity documents yet
                </p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default ViewSingleUser;
